import * as React from 'react'
import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { useDashboardStore } from '@/store/dashboardStore'
import type { DashboardRow } from '@/hooks/useDashboard'
import { SIGNAL_LABELS, type SignalKind } from '@/hooks/useDashboard'
import { MultiSelectDropdown } from './MultiSelectDropdown'
import type { SortingState } from '@tanstack/react-table'

// Render order for the action-signal filter — kept separate from the
// SignalKind union so we can omit 'unknown' from the picker.
const SIGNAL_FILTER_ORDER: SignalKind[] = ['order', 'liquidate', 'ok', 'no_action', 'stop', 'high_returns']

// Shopify product status values
const STATUS_OPTIONS = [
  { value: 'active', count: undefined as number | undefined },
  { value: 'draft', count: undefined as number | undefined },
  { value: 'archived', count: undefined as number | undefined },
]

// "Check history" flag values (the product-level flag), with their display labels.
const SPIKE_LABELS: Record<string, string> = { flagged: 'Needs checking', not_flagged: 'Fine' }

interface FilterRailProps {
  rows: DashboardRow[]
  sorting: SortingState
}

export function FilterRail({ rows, sorting }: FilterRailProps) {
  const { filters, setFilters, clearFilters, saveCurrentAsView, railCollapsed, toggleRailCollapsed } =
    useDashboardStore()
  const [saveOpen, setSaveOpen] = useState(false)
  const [viewName, setViewName] = useState('')

  // All option lists derive from RAW rows so they stay stable as the user
  // narrows other filters.
  const vendorOptions = React.useMemo(() => {
    const counts = new Map<string, number>()
    for (const r of rows) {
      if (r.vendor) counts.set(r.vendor, (counts.get(r.vendor) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, count }))
  }, [rows])

  const productTypeOptions = React.useMemo(() => {
    const counts = new Map<string, number>()
    for (const r of rows) {
      if (r.product_type) counts.set(r.product_type, (counts.get(r.product_type) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, count }))
  }, [rows])

  const tagOptions = React.useMemo(() => {
    const counts = new Map<string, number>()
    for (const r of rows) {
      for (const tag of r.tags ?? []) {
        counts.set(tag, (counts.get(tag) ?? 0) + 1)
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([value, count]) => ({ value, count }))
  }, [rows])

  const spikeOptions = React.useMemo(() => {
    const flagged = rows.filter((r) => r.product_lifetime_spike).length
    return [
      { value: SPIKE_LABELS.flagged, count: flagged },
      { value: SPIKE_LABELS.not_flagged, count: rows.length - flagged },
    ]
  }, [rows])

  const spikeAllOptions = React.useMemo(() => {
    const flagged = rows.filter((r) => r.product_lifetime_spike_all).length
    return [
      { value: SPIKE_LABELS.flagged, count: flagged },
      { value: SPIKE_LABELS.not_flagged, count: rows.length - flagged },
    ]
  }, [rows])

  // Signal/status options carry display labels; we map back to internal
  // values in the onChange.
  const signalDisplayOptions = SIGNAL_FILTER_ORDER.map((k) => ({
    value: SIGNAL_LABELS[k],
  }))
  const statusDisplayOptions = STATUS_OPTIONS.map((o) => ({
    value: o.value.charAt(0).toUpperCase() + o.value.slice(1),
    count: o.count,
  }))

  function handleSaveView() {
    const name = viewName.trim()
    if (!name) return
    saveCurrentAsView(name, sorting)
    toast.success(`View "${name}" saved`)
    setSaveOpen(false)
    setViewName('')
  }

  if (railCollapsed) {
    return (
      <button
        type="button"
        className="dash-rail-collapsed"
        onClick={toggleRailCollapsed}
        title="Show filters"
        aria-label="Show filters"
        aria-expanded={false}
      >
        <ChevronRight className="h-4 w-4" />
        <span className="dash-rail-collapsed-label">Filters</span>
      </button>
    )
  }

  return (
    <aside className="dash-rail" aria-label="Filters">
      <div className="dash-rail-head">
        <h4 className="dash-rail-h">Filters</h4>
        <button
          type="button"
          className="dash-rail-collapse-btn"
          onClick={toggleRailCollapsed}
          title="Hide filters"
          aria-label="Hide filters"
          aria-expanded={true}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>

      {/* Action signal */}
      <div className="dash-rail-group">
        <MultiSelectDropdown
          label="Action signal"
          options={signalDisplayOptions}
          selected={filters.signal.map((k) => SIGNAL_LABELS[k as SignalKind] ?? k)}
          onChange={(labels) => {
            const vals = labels.map((l) => {
              const entry = (Object.entries(SIGNAL_LABELS) as [SignalKind, string][]).find(
                ([, v]) => v === l
              )
              return entry ? entry[0] : l
            })
            setFilters({ signal: vals })
          }}
          placeholder="All signals"
          data-testid="filter-signal"
        />
      </div>

      {/* Status */}
      <div className="dash-rail-group">
        <MultiSelectDropdown
          label="Status"
          options={statusDisplayOptions}
          selected={filters.status.map((s) => s.charAt(0).toUpperCase() + s.slice(1))}
          onChange={(labels) => setFilters({ status: labels.map((l) => l.toLowerCase()) })}
          placeholder="All statuses"
          data-testid="filter-status"
        />
      </div>

      {/* Vendor */}
      <div className="dash-rail-group">
        <MultiSelectDropdown
          label="Vendor"
          options={vendorOptions}
          selected={filters.vendor}
          onChange={(v) => setFilters({ vendor: v })}
          placeholder="All vendors"
          data-testid="filter-vendor"
        />
      </div>

      {/* Product type */}
      <div className="dash-rail-group">
        <MultiSelectDropdown
          label="Product type"
          options={productTypeOptions}
          selected={filters.product_type}
          onChange={(v) => setFilters({ product_type: v })}
          placeholder="All types"
          data-testid="filter-product-type"
        />
      </div>

      {/* Include tags */}
      <div className="dash-rail-group">
        <MultiSelectDropdown
          label="Include tags"
          options={tagOptions}
          selected={filters.tags_include}
          onChange={(v) => setFilters({ tags_include: v })}
          placeholder="+ add tag"
          chipVariant="default"
          data-testid="filter-tags-include"
        />
      </div>

      {/* Exclude tags */}
      <div className="dash-rail-group">
        <MultiSelectDropdown
          label="Exclude tags"
          options={tagOptions}
          selected={filters.tags_exclude}
          onChange={(v) => setFilters({ tags_exclude: v })}
          placeholder="+ add tag"
          chipVariant="exclude"
          data-testid="filter-tags-exclude"
        />
      </div>

      {/* Check history (display-only flag; see the column tooltip) */}
      <div className="dash-rail-group">
        <MultiSelectDropdown
          label="Check history"
          options={spikeOptions}
          selected={filters.spike.map((v) => SPIKE_LABELS[v] ?? v)}
          onChange={(labels) =>
            setFilters({
              spike: labels.map(
                (l) => Object.keys(SPIKE_LABELS).find((k) => SPIKE_LABELS[k] === l) ?? l
              ),
            })
          }
          placeholder="All rows"
          data-testid="filter-spike"
        />
      </div>

      {/* Check history (all): the same flag before hiding rows with Rec Qty 0 */}
      <div className="dash-rail-group">
        <MultiSelectDropdown
          label="Check history (all)"
          options={spikeAllOptions}
          selected={filters.spike_all.map((v) => SPIKE_LABELS[v] ?? v)}
          onChange={(labels) =>
            setFilters({
              spike_all: labels.map(
                (l) => Object.keys(SPIKE_LABELS).find((k) => SPIKE_LABELS[k] === l) ?? l
              ),
            })
          }
          placeholder="All rows"
          data-testid="filter-spike-all"
        />
      </div>

      {/* Days coverage range */}
      <div className="dash-rail-group">
        <label className="dash-rail-label">Days coverage</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="number"
            aria-label="Min days coverage"
            placeholder="Min"
            className="dash-rail-input"
            value={filters.cov_min ?? ''}
            onChange={(e) => setFilters({ cov_min: e.target.value ? Number(e.target.value) : null })}
          />
          <input
            type="number"
            aria-label="Max days coverage"
            placeholder="Max"
            className="dash-rail-input"
            value={filters.cov_max ?? ''}
            onChange={(e) => setFilters({ cov_max: e.target.value ? Number(e.target.value) : null })}
          />
        </div>
      </div>

      {/* Footer actions */}
      <div className="dash-rail-group">
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={clearFilters}
            className="dash-rail-btn"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={() => setSaveOpen((o) => !o)}
            className="dash-rail-btn"
          >
            Save view
          </button>
        </div>

        {saveOpen && (
          <div style={{ marginTop: 8, display: 'flex', gap: 4 }}>
            <input
              autoFocus
              type="text"
              placeholder="View name…"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveView()
                if (e.key === 'Escape') setSaveOpen(false)
              }}
              className="dash-rail-input"
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={handleSaveView}
              className="dash-rail-btn dash-rail-btn-primary"
            >
              Save
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}
